import { ContentItem } from '@/types/cms';

export const blogs: Record<string, ContentItem> = {
    "edtechx-awards": {
        "id": "edtechx-awards",
        "type": "blog",
        "category": "award",
        "title": "StudyStash's Journey to EdTechX Finalist (2024)",
        "description": "Ben Ward and Jonathan Graham, co-founders of StudyStash, were recognised as finalists in the EdTechX Awards for their innovative learning platform.",
        "slug": "edtechx-awards",
        "publishDate": "2024-01-10",
        "readingTime": "6 min read",
        "author": {
            "name": "Ben Ward",
            "image": "/team/ben.jpeg"
        },
        "hero": {
            "image": "/case-studies/EdTechX.png",
            "imageAlt": "The 2024 EdTechX Awards in London"
        },
        "contentBlocks": [
            {
                "type": "heading",
                "level": 1,
                "text": "From Concept to EdTechX Finalist"
            },
            {
                "type": "heading",
                "level": 2,
                "text": "Competing with Global EdTech Giants"
            },
            {
                "type": "paragraph",
                "text": "Ben Ward and Jonathan Graham, two forward-thinking students from the University of Birmingham, have developed StudyStash-a groundbreaking EdTech platform utilising AI and neuroscience that's already making waves by onboarding UK universities such as UoB. Competing with global giants like Duolingo and Quizlet, StudyStash is revolutionising higher education with its personalised approach to learning.",
            },
            {
                "type": "statistics",
                "stats": [
                    {
                        "value": "Global",
                        "title": "Competition",
                        "description": "with Duolingo and Quizlet",
                        "color": "blue"
                    },
                    {
                        "value": "AI + Neuroscience",
                        "title": "Powered",
                        "description": "personalised learning",
                        "color": "green"
                    },
                    {
                        "value": "UK Universities",
                        "title": "Already",
                        "description": "onboarded and engaged",
                        "color": "purple"
                    }
                ],
                "layout": "grid"
            },
            {
                "type": "heading",
                "level": 2,
                "text": "The Vision",
            },
            {
                "type": "paragraph",
                "text": "Our vision is to create a world where learning is smarter, allowing everyone to achieve the best possible outcomes from their investment in personal growth. We revolutionise the learning journey by making content and processes adaptive to each individual's unique needs.",
            },
            {
                "type": "heading",
                "level": 3,
                "text": "Dedicated to Excellence",
            },
            {
                "type": "paragraph",
                "text": "We're dedicated to delivering brilliant outcomes for every person, regardless of their starting point or learning goal. This commitment to inclusive, personalised education is what sets StudyStash apart in the competitive EdTech landscape.",
            },
            {
                "type": "heading",
                "level": 2,
                "text": "Addressing Student Challenges"
            },
            {
                "type": "heading",
                "level": 3,
                "text": "The University Experience Problem"
            },
            {
                "type": "paragraph",
                "text": "Some students are dissatisfied with their university experience, facing up to 300 pages of disengaging content. For many, learning feels disorganised and unengaging, especially for neurodivergent and international students.",
            },
            {
                "type": "paragraph",
                "text": "Our mission is to transform this experience, ensuring that every learner feels empowered and confident. StudyStash combines AI and neuroscience to personalise learning journeys, adapting broadcasted content to fit individual needs.",
            },
            {
                "type": "heading",
                "level": 2,
                "text": "The Technology Advantage"
            },
            {
                "type": "heading",
                "level": 3,
                "text": "AI-Driven Analytics"
            },
            {
                "type": "paragraph",
                "text": "By using AI-driven analytics, StudyStash identifies concepts you struggle with and helps you improve your knowledge effectively. Our platform learns about you as you learn, optimising content to maximise your outcomes.",
            },
            {
                "type": "paragraph",
                "text": "We want to eliminate the one-size-fits-all approach and create a truly personalised learning environment. This proactive approach ensures that you can track your progress and stay engaged, shifting the game from boring, broadcast content to a more engaging, adaptive learning experience.",
            }
        ],
        "seo": {
            "title": "StudyStash EdTechX Awards Finalist | StudyStash",
            "description": "Ben Ward and Jonathan Graham, co-founders of StudyStash, were recognised as finalists in the EdTechX Awards for their innovative learning platform.",
            "canonical": "https://studystash.com/about/blogs/edtechx-awards"
        },
        "relatedContent": [
            "9-to-thrive-podcast",
            "innovators-uncensored",
            "university-of-birmingham"
        ]
    },
    "9-to-thrive-podcast": {
        "id": "9-to-thrive-podcast",
        "type": "blog",
        "category": "news",
        "title": "Jonathan's Journey: The 9 to Thrive Podcast",
        "description": "Jonathan shares the journey of building StudyStash as a student, the challenges faced, and the pivotal role of the University of Birmingham.",
        "slug": "9-to-thrive-podcast",
        "publishDate": "2024-03-15",
        "readingTime": "5 min read",
        "author": {
            "name": "Jonathan Graham",
            "image": "/team/jonathan.png"
        },
        "hero": {
            "image": "/case-studies/9-to-thrive.png",
            "imageAlt": "The 9 to Thrive Podcast"
        },
        "contentBlocks": [
            {
                "type": "heading",
                "level": 1,
                "text": "Building StudyStash as a Student Entrepreneur",
            },
            {
                "type": "podcast",
                "embedUrl": "https://open.spotify.com/embed/episode/1ks9OjgsM7oVt6TMxiTS9n?utm_source=generator",
                "title": "StudyStash: Life as a Studentrepreneur",
                "description": "Listen to Jonathan's full interview on the 9 to Thrive podcast",
                "platform": "spotify"
            },
            {
                "type": "heading",
                "level": 2,
                "text": "From University Halls to EdTech Innovation",
            },
            {
                "type": "paragraph",
                "text": "Jonathan Graham, CTO and co-founder of StudyStash, shares his journey of building an ed-tech platform while studying computer science at the University of Birmingham. From a small coding project to a platform engaging over 5,000 students, discover how StudyStash is revolutionising higher education.",
            },
            {
                "type": "statistics",
                "stats": [
                    {
                        "value": "5,000+",
                        "title": "Students",
                        "description": "students engaged across all five colleges at UoB",
                        "color": "blue"
                    },
                    {
                        "value": "2+ hours",
                        "title": "Average Usage",
                        "description": "average usage per student",
                        "color": "green"
                    },
                    {
                        "value": "4th year",
                        "title": "Student",
                        "description": "computer science student building the future",
                        "color": "purple"
                    }
                ],
                "layout": "grid"
            },
            {
                "type": "heading",
                "level": 2,
                "text": "The StudyStash Vision",
            },
            {
                "type": "paragraph",
                "text": "StudyStash is an ed-tech platform that combines neuroscience and AI to revolutionise how students learn in higher education. It started as a small coding project when Ben and Jonathan were overwhelmed by the information at university. Initially, it was a tool to help them and their friends, but they soon realised it had the potential to be much more.",
            },
            {
                "type": "heading",
                "level": 3,
                "text": "From Personal Tool to Business",
            },
            {
                "type": "paragraph",
                "text": "The moment of inspiration came when Jonathan and Ben went to the wrong lecture room and decided to stay and start coding. It was in that moment they created the first version of StudyStash. They initially built it to help themselves manage learning better, but when they shared it with others, they realised it could be really useful for many students.",
            },
            {
                "type": "heading",
                "level": 2,
                "text": "The University of Birmingham Pilot"
            },
            {
                "type": "heading",
                "level": 3,
                "text": "Scaling Impact",
            },
            {
                "type": "paragraph",
                "text": "The pilot has been incredibly exciting, engaging over 5,000 students across all five colleges at the university. Students are averaging more than two hours of usage, demonstrating strong engagement with the platform.",
            },
            {
                "type": "paragraph",
                "text": "Additionally, they're running a research study alongside the pilot to measure the effectiveness of StudyStash compared to other learning tools. This research will be key as they expand to other institutions.",
            },
            {
                "type": "heading",
                "level": 2,
                "text": "The Student Entrepreneur Journey",
            },
            {
                "type": "heading",
                "level": 3,
                "text": "Building While Learning",
            },
            {
                "type": "paragraph",
                "text": "Jonathan has always been interested in computer science and his parents have always said he was very independent and liked doing his own thing. He's always had a bit of something in him that made him want to start his own business.",
            },
            {
                "type": "paragraph",
                "text": "The journey from a university coding project to a platform serving thousands of students demonstrates the power of student-led innovation and the importance of solving real problems you experience firsthand.",
            },
        ],
        "seo": {
            "title": "StudyStash Co-Founder on 9 to Thrive Podcast | StudyStash",
            "description": "Jonathan Graham shares insights on building StudyStash as a student entrepreneur on the 9 to Thrive podcast.",
            "canonical": "https://studystash.com/about/blogs/9-to-thrive-podcast"
        },
        "relatedContent": [
            "innovators-uncensored",
            "edtechx-awards",
            "university-of-birmingham"
        ]
    },
    "innovators-uncensored": {
        "id": "innovators-uncensored",
        "type": "blog",
        "category": "news",
        "title": "How StudyStash is Transforming University Learning with Neuroscience and AI",
        "description": "StudyStash is revolutionising university learning by combining neuroscience and AI to create a personalised, adaptive study experience that evolves with each student.",
        "slug": "innovators-uncensored",
        "publishDate": "2024-02-20",
        "readingTime": "4 min read",
        "author": {
            "name": "StudyStash Team",
            "image": "/team/ben.jpeg"
        },
        "hero": {
            "image": "/team.jpg",
            "imageAlt": "The StudyStash team at their WM offices"
        },
        "contentBlocks": [
            {
                "type": "heading",
                "level": 1,
                "text": "Revolutionising University Learning"
            },
            {
                "type": "heading",
                "level": 2,
                "text": "From Idea to Innovators Uncensored",
            },
            {
                "type": "paragraph",
                "text": "StudyStash began in university halls, sparked by the co-founders' shared experiences and commitment to building an adaptive learning tool. Their collaboration turned into a solution rooted in student insight, tech expertise, and a passion for education.",
            },
            {
                "type": "statistics",
                "stats": [
                    {
                        "value": "Adaptive",
                        "title": "",
                        "description": "learning platform that evolves with each student",
                        "hasGradient": true,
                        "gradientFrom": "from-blue-500",
                        "gradientTo": "to-purple-600"
                    },
                    {
                        "value": "Free Access",
                        "title": "",
                        "description": "funded directly by universities for students"
                    },
                    {
                        "value": "Neuroscience",
                        "title": "",
                        "description": "driven approach to personalised learning"
                    }
                ],
                "layout": "grid",
            },
            {
                "type": "heading",
                "level": 2,
                "text": "The Breakthrough Moment",
            },
            {
                "type": "paragraph",
                "text": "The team's initial breakthrough came through a simple demo video shared on LinkedIn, which caught the attention of universities. This demonstrated the power of straightforward outreach in building momentum and fostering critical partnerships.",
            },
            {
                "type": "heading",
                "level": 3,
                "text": "Strategic Growth",
            },
            {
                "type": "paragraph",
                "text": "The founders have focused on hiring talented individuals, reinvesting all profits into the product and team. By foregoing salaries, they've prioritised growth and development over immediate financial gain.",
            },
            {
                "type": "heading",
                "level": 2,
                "text": "Learning from Early Customers",
            },
            {
                "type": "heading",
                "level": 3,
                "text": "Perfection Isn't Required",
            },
            {
                "type": "paragraph",
                "text": "StudyStash learned early that perfection isn't required to start. Initial customers were drawn to the vision and core concept, proving that early, functional versions can succeed and improve through feedback.",
            },
            {
                "type": "paragraph",
                "text": "This approach has allowed them to iterate quickly and build a product that truly serves student needs, rather than getting caught up in perfecting features that may not matter to users.",
            },
            {
                "type": "heading",
                "level": 2,
                "text": "The Neuroscience Advantage",
            },
            {
                "type": "heading",
                "level": 2,
                "text": "Personalised Learning at Scale",
            },
            {
                "type": "paragraph",
                "text": "Unlike passive study tools, StudyStash's platform evolves with each student, tailoring content to their preferences and challenges. This neuroscience-driven approach creates a truly personalised learning environment that adapts to individual learning patterns.",
            },
            {
                "type": "paragraph",
                "text": "The platform is funded directly by universities to provide free access to students, ensuring that innovative learning tools are accessible to all, regardless of financial background.",
            },
            {
                "type": "cta-button",
                "text": "Read Full Article",
                "href": "https://innovatorsuncensored.beehiiv.com/p/how-this-start-up-is-changing-how-students-learn-by-blending-neuroscience-and-ai",
                "variant": "outline",
                "size": "lg"
            }
        ],
        "seo": {
            "title": "StudyStash Featured in Innovators Uncensored | StudyStash",
            "description": "How StudyStash is transforming university learning with neuroscience and AI, featured in Innovators Uncensored.",
            "canonical": "https://studystash.com/about/blogs/innovators-uncensored"
        },
        "relatedContent": [
            "edtechx-awards",
            "9-to-thrive-podcast",
            "university-of-birmingham"
        ]
    },
    "eab-partnership-announcement": {
        id: "eab-partnership-announcement",
        type: "blog",
        category: "partnership",
        title: "StudyStash Partners with EAB",
        description: "StudyStash announces strategic partnership with EAB to enhance student success initiatives across higher education institutions.",
        slug: "eab-partnership-announcement",
        publishDate: "2024-12-10",
        readingTime: "4 min read",
        author: {
            name: "Joanna Williams",
            image: "/team/joanna_williams.jpeg"
        },
        hero: {
            image: "/EAB-blue-bg.png",
            imageAlt: "EAB logo"
        },

        contentBlocks: [
            {
                type: "heading",
                level: 1,
                text: "Strategic Partnership for Student Success"
            },
            {
                type: "paragraph",
                text: "We're excited to announce our strategic partnership with EAB, a leading provider of technology and services that help colleges and universities improve student outcomes. This collaboration will enhance our ability to support institutions in their mission to boost student success and retention."
            },
            {
                type: "statistics",
                stats: [
                    {
                        value: "Strategic",
                        title: "Partnership",
                        description: "partnership with EAB",
                        color: "blue"
                    },
                    {
                        value: "Student Success",
                        title: "Mission",
                        description: "our shared mission",
                        color: "green"
                    },
                    {
                        value: "Higher Education",
                        title: "Focus",
                        description: "institutions supported",
                        color: "purple"
                    }
                ],
                layout: "grid"
            },
            {
                type: "heading",
                level: 2,
                text: "How the Partnership Began"
            },
            {
                type: "paragraph",
                text: "The partnership originated from StudyStash's successful pilot at the University of Birmingham, involving over 5,000 students. The impressive results caught the attention of Deborah Longworth, Pro-Vice-Chancellor of Education at the University of Birmingham, who introduced StudyStash to EAB, recognizing the potential for scaling these proven outcomes across higher education institutions globally."
            },
            {
                type: "quote",
                text: "This partnership with EAB represents a pivotal moment for StudyStash and higher education.",
                author: "Joanna Williams",
                title: "Senior Marketing Executive",
                image: "/team/joanna_williams.jpeg"
            },
            {
                type: "heading",
                level: 2,
                text: "Partnership Overview"
            },
            {
                type: "paragraph",
                text: "This partnership brings together StudyStash's innovative AI-powered learning platform with EAB's extensive experience in student success analytics and institutional support. Together, we're creating comprehensive solutions that help universities identify at-risk students earlier and provide targeted interventions."
            },
            {
                type: "paragraph",
                text: "Both organizations share a commitment to improving student outcomes through data-driven insights and personalized support. This partnership allows us to combine our strengths in learning technology with EAB's expertise in student success management."
            },
            {
                type: "heading",
                level: 2,
                text: "Benefits for Institutions"
            },
            {
                type: "paragraph",
                text: "Institutions will benefit from integrated analytics that combine StudyStash's learning data with EAB's student success metrics, providing a more complete picture of student engagement and performance."
            },
            {
                type: "paragraph",
                text: "This comprehensive approach enables earlier identification of students who may need additional support, allowing for more proactive intervention strategies."
            },
            {
                type: "heading",
                level: 2,
                text: "Download the Case Study"
            },
            {
                type: "paragraph",
                text: "Learn more about how StudyStash and EAB are working together to transform student success outcomes across higher education institutions."
            },
            {
                type: "quote",
                text: "Let me say again that you and your team are doing something really special. I've been talking with a lot of universities about where they're at on their AI journey, and the kind of support you're providing isn't on any of their radars.",
                author: "Gary Guadagnolo",
                title: "EAB Senior Research Director and Higher Education Expert",
                image: "/gary.png"
            }
        ],

        seo: {
            title: "StudyStash Partners with EAB | StudyStash",
            description: "StudyStash announces strategic partnership with EAB to enhance student success initiatives across higher education institutions.",
            canonical: "https://studystash.com/about/blogs/eab-partnership"
        },

        relatedContent: ["eab-connected25", "educause"]
    },
    "lords-mayors-award": {
        id: "lords-mayors-award",
        type: "blog",
        category: "award",
        title: "StudyStash Nominated for the Lord Mayor's Award",
        description: "We're proud to share that StudyStash has been nominated for the Lord Mayor's Award by Dash Virdee. This recognition highlights our commitment to making a difference in education across the region.",
        slug: "lords-mayors-award",
        publishDate: "2025-01-15",
        readingTime: "4 min read",
        author: {
            name: "Joanna Williams",
            image: "/team/joanna_williams.jpeg"
        },
        hero: {
            image: "/lords_mayors_award.png",
            imageAlt: "Lord Mayor's Award logo"
        },

        contentBlocks: [
            {
                type: "heading",
                level: 1,
                text: "Supporting Schools, Colleges and Universities"
            },
            {
                type: "paragraph",
                text: "From the beginning, StudyStash has worked closely with schools, colleges, and universities to provide engaging tools that help students learn more effectively. Our platform is designed to complement classroom teaching and give learners a simple, modern way to prepare for exams and coursework."
            },
            {
                type: "statistics",
                stats: [
                    {
                        value: "West Midlands",
                        title: "Location",
                        description: "based company",
                        color: "blue"
                    },
                    {
                        value: "Dash Virdee",
                        title: "Nominator",
                        description: "nominated StudyStash for the award",
                        color: "green"
                    },
                    {
                        value: "Local Education",
                        title: "Focus",
                        description: "our commitment",
                        color: "purple"
                    }
                ],
                layout: "grid"
            },
            {
                type: "heading",
                level: 2,
                text: "A West Midlands Based Company"
            },
            {
                type: "paragraph",
                text: "As a West Midlands-based company, we're rooted in the communities we serve. Education is at the heart of our region's future, and we're passionate about helping learners across the Midlands unlock their potential."
            },
            {
                type: "heading",
                level: 2,
                text: "Commitment to Local Education"
            },
            {
                type: "paragraph",
                text: "This nomination reflects not only our work so far, but also our ambition to continue supporting learners and institutions across the West Midlands for years to come."
            },
            {
                type: "quote",
                text: "We want to support local education institutes for higher education across the West Midlands and are deeply committed to this mission - it's our home, and we believe in giving back to the community that shaped us.",
                author: "Joanna Williams",
                title: "Senior Marketing Executive",
                image: "/team/joanna_williams.jpeg"
            }
        ],

        seo: {
            title: "StudyStash Nominated for Lord Mayor's Award | StudyStash",
            description: "We're proud to share that StudyStash has been nominated for the Lord Mayor's Award by Dash Virdee. This recognition highlights our commitment to making a difference in education across the region.",
            canonical: "https://studystash.com/about/blogs/lords-mayors-award"
        },

        relatedContent: ["eab-connected25", "educause"]
    },
    "supporting-neurodiverse-learners-ai-edtech": {
        id: "supporting-neurodiverse-learners-ai-edtech",
        type: "blog",
        category: "news",
        title: "Supporting Neurodiverse Learners with AI-Powered EdTech",
        description: "How AI-powered educational tools can help create more flexible, inclusive learning environments for neurodiverse students through personalisation, structure, and timely feedback.",
        slug: "supporting-neurodiverse-learners-ai-edtech",
        publishDate: "2026-03-17",
        readingTime: "7 min read",
        author: {
            name: "Joanna Williams",
            image: "/team/joanna_williams.jpeg"
        },
        hero: {
            image: "/student-teacher.jpg",
            imageAlt: "Supporting Neurodiverse Learners with AI-Powered EdTech"
        },
        contentBlocks: [
            {
                type: "paragraph",
                text: "Education has made important progress in recognising that students do not all think, learn, or communicate in the same way. Neurodiversity includes conditions such as dyslexia, dyscalculia, dyspraxia, ADHD, autism, and Tourette's syndrome, and reflects natural variation in how people process information and experience the world."
            },
            {
                type: "paragraph",
                text: "These differences are not problems to be fixed. They are part of the diversity of human thinking. Yet many learning environments still rely on a narrow model: long lectures, dense reading, and fixed schedules. While this works for some learners, it can create barriers for others."
            },
            {
                type: "image",
                src: "/neurodiversity_week.png",
                alt: "Neurodiversity Week visual representing inclusive learning",
                caption: "Recognising and celebrating neurodiversity is central to building more inclusive education"
            },
            {
                type: "paragraph",
                text: "This is where AI-supported educational technology can make a practical difference. Platforms such as StudyStash are part of a broader shift toward learning environments that adapt to different ways of thinking, rather than expecting every student to learn in the same way."
            },
            {
                type: "heading",
                level: 2,
                text: "Why Flexibility Matters"
            },
            {
                type: "paragraph",
                text: "Neurodiverse learners often bring significant strengths to education, including creativity, pattern recognition, problem solving, and deep focus on subjects that interest them. The difficulty is rarely capability. More often, it comes from the way learning is structured."
            },
            {
                type: "paragraph",
                text: "Traditional teaching methods can present large amounts of information at once and may limit opportunities to revisit explanations or approach material in alternative ways. AI-supported tools offer an alternative by adapting the learning process to suit individual needs, helping students engage with content in ways that work for them."
            },
            {
                type: "heading",
                level: 2,
                text: "Building Confidence Through Independent Learning"
            },
            {
                type: "paragraph",
                text: "Confidence plays a major role in participation. For some neurodiverse students, previous academic struggles or fear of falling behind peers can make it harder to ask questions or contribute in class."
            },
            {
                type: "paragraph",
                text: "Digital study tools can provide a quieter space to work through material. Students can revisit explanations, practise skills, and explore ideas privately, without the immediate pressure of classroom participation. Working at their own pace often encourages experimentation and persistence."
            },
            {
                type: "heading",
                level: 2,
                text: "Personalised Learning and Cognitive Load"
            },
            {
                type: "paragraph",
                text: "One of the most promising aspects of AI in education is personalisation. Instead of presenting information in a single format, AI-supported platforms can adjust pacing, difficulty, and how explanations are delivered."
            },
            {
                type: "paragraph",
                text: "The aim is not to oversimplify learning, but to make complex ideas easier to engage with. For example, learners with ADHD may benefit from shorter, structured study segments that support sustained focus. Learners with dyslexia may benefit from clearer summaries, structured notes, or audio support alongside written text."
            },
            {
                type: "paragraph",
                text: "These approaches align with Universal Design for Learning principles, which promote multiple ways for students to access and engage with information."
            },
            {
                type: "heading",
                level: 2,
                text: "Supporting Organisation and Task Management"
            },
            {
                type: "paragraph",
                text: "Many neurodiverse learners also experience challenges with executive functioning, including organising work, planning tasks, and managing deadlines. Coursework can quickly feel overwhelming when the steps involved are unclear."
            },
            {
                type: "paragraph",
                text: "AI-supported platforms can reduce this pressure by breaking tasks into smaller stages and providing tools such as reminders, structured study plans, and prioritisation guidance. This helps students focus on one step at a time, rather than feeling blocked by the full task."
            },
            {
                type: "image",
                src: "/app/calendar_and_streaks.jpeg",
                alt: "Calendar and streak tracking interface supporting student planning and consistency",
                caption: "Structured planning tools can make study routines clearer and easier to sustain."
            },
            {
                type: "heading",
                level: 2,
                text: "Learning Through Immediate Feedback"
            },
            {
                type: "paragraph",
                text: "Timely feedback is one of the strongest drivers of learning, yet in traditional settings students may wait days or weeks to receive it. AI-supported platforms can respond much more quickly."
            },
            {
                type: "bullet-list",
                style: "checkmarks",
                items: [
                    "Immediate feedback helps students identify misunderstandings.",
                    "Students can correct mistakes early, before gaps compound.",
                    "Learners can track progress over time and build confidence step by step."
                ]
            },
            {
                type: "paragraph",
                text: "For learners who may have experienced frustration in the past, seeing steady progress can be especially motivating."
            },
            {
                type: "heading",
                level: 2,
                text: "What Students Are Telling Us"
            },
            {
                type: "paragraph",
                text: "Student feedback regularly highlights how meaningful small changes in learning design can be. One undergraduate student shared that using a structured study platform helped reduce burnout during demanding coursework periods. An MBA student described finding long reading lists difficult to sustain, but reported that shorter, structured segments improved engagement."
            },
            {
                type: "paragraph",
                text: "Sometimes relatively small changes in how learning is structured can make a significant difference."
            },
            {
                type: "heading",
                level: 2,
                text: "Looking Ahead"
            },
            {
                type: "paragraph",
                text: "Supporting neurodiverse learners is not simply about accommodation. It is about recognising that people think and learn in different ways, and designing education systems that reflect that reality."
            },
            {
                type: "paragraph",
                text: "AI-powered educational tools are not a complete solution, but they offer a strong opportunity to create learning environments that are more flexible, responsive, and inclusive. When education works better for neurodiverse students, it usually improves the experience for everyone."
            },
            {
                type: "sources",
                title: "References",
                items: [
                    {
                        id: "oecd-ai-neurodivergent-learners-vet",
                        title: "AI to support neurodivergent learners in vocational education and training",
                        authors: ["OECD"],
                        url: "https://www.oecd.org/en/publications/ai-to-support-neurodivergent-learners-in-vocational-education-and-training_718d7522-en/full-report/component-3.html",
                        type: "article",
                        publication: "OECD Publishing"
                    },
                    {
                        id: "ncce-supporting-neurodiverse-learners-tech",
                        title: "Supporting neurodiverse learners with technology",
                        authors: ["NCCE"],
                        url: "https://ncce.org/supporting-neurodiverse-learners-with-technology/",
                        type: "website",
                        publication: "National Centre for Computing Education"
                    },
                    {
                        id: "kaplan-ai-neurodivergent-learners",
                        title: "Five ways AI can help neurodivergent learners",
                        authors: ["Kaplan"],
                        url: "https://kaplan.co.uk/blog/learning/five-ways-ai-can-help-neurodivergent-learners",
                        type: "article",
                        publication: "Kaplan"
                    },
                    {
                        id: "systematic-review-ai-special-education",
                        title: "Systematic review on AI in special education: enhancing learning for neurodiverse students",
                        authors: ["Dinesh Deckker"],
                        url: "https://www.researchgate.net/publication/389352569_SYSTEMATIC_REVIEW_ON_AI_IN_SPECIAL_EDUCATION_ENHANCING_LEARNING_FOR_NEURODIVERSE_STUDENTS",
                        type: "article",
                        publication: "EPRA International Journal of Multidisciplinary Research (IJMR)"
                    },
                    {
                        id: "intechopen-ai-neurodiverse-learning",
                        title: "Neurodivergent by Design: Using AI to Honor and Support Learning Differences",
                        authors: ["Christina Costa"],
                        url: "https://www.intechopen.com/online-first/1230197",
                        type: "article",
                        publication: "IntechOpen"
                    }
                ]
            }
        ],
        seo: {
            title: "Supporting Neurodiverse Learners with AI-Powered EdTech | StudyStash",
            description: "Explore how AI-powered EdTech can support neurodiverse learners through personalised pathways, better organisation, and immediate feedback.",
            canonical: "https://studystash.com/about/blogs/supporting-neurodiverse-learners-ai-edtech"
        },
        relatedContent: ["closing-teaching-learning-loop", "innovators-uncensored", "edtechx-awards"]
    },
    "closing-teaching-learning-loop": {
        id: "closing-teaching-learning-loop",
        type: "blog",
        category: "news",
        title: "Closing the Teaching-Learning Loop in Learning Support Platforms",
        description: "How learning-intelligence platforms can convert study data into actionable insights, enabling timely intervention and tracking outcomes to improve student retention and welfare.",
        slug: "closing-teaching-learning-loop",
        publishDate: "2025-01-25",
        readingTime: "8 min read",
        author: {
            name: "Joanna Williams",
            image: "/team/joanna_williams.jpeg"
        },
        hero: {
            image: "/case-studies/he_analytics_placeholder.jpg",
            imageAlt: "Closing the Teaching-Learning Loop in Learning Support Platforms"
        },
        contentBlocks: [
            {
                type: "heading",
                level: 1,
                text: "Closing the Teaching-Learning Loop in Learning Support Platforms"
            },
            {
                type: "paragraph",
                text: "Student retention and welfare are now central to higher‑education strategy, not just for reputational or financial reasons but for sustaining meaningful student success. But too many platforms stop at data collection. The real breakthrough lies in closing the loop: converting study data into insights, enabling timely intervention, and tracking outcomes."
            },
            {
                type: "quote",
                text: "Ignoring the impact of analytics is a mistake: universities with real-time actionable information on student engagement can effectively target those areas where risks to continuation are evident – whether at the programme or cohort level, or defined by protected characteristics or risks to equality of opportunity.",
                author: "Rachel Maxwell",
                title: "Principal Advisor at Kortext",
                image: "/case-studies/Rachel Maxwell.jpeg"
            },
            {
                type: "heading",
                level: 2,
                text: "The problem: Why traditional LMS fall short"
            },
            {
                type: "paragraph",
                text: "Most institutions utilise learning management systems or digital content platforms, gathering detailed engagement data. But in many cases, that information remains underused. Reports are generated, dashboards are reviewed, and trends are discussed, yet little feedback or individualised action follows."
            },
            {
                type: "paragraph",
                text: "In the UK, this gap is reflected in the data. Recent commentary places the national drop-out rate at around 6.3 per cent, while the Complete University Guide reports that the average continuation rate fell from 92.2 per cent in 2025 to 89.7 per cent in 2026 (Complete University Guide, 2026). These figures highlight a persistent issue: too many students disengage before completing their studies, often without targeted support or early-warning intervention."
            },
            {
                type: "heading",
                level: 2,
                text: "From data to action"
            },
            {
                type: "paragraph",
                text: "A new generation of learning-intelligence approaches is emerging to connect the full cycle of engagement data, behavioural insight, risk signalling, staff response, and outcome tracking. Rather than merely observing, these systems are designed to respond."
            },
            {
                type: "paragraph",
                text: "Adaptive analytics can identify when a student's study time, revision patterns or engagement habits begin to diverge from those typically associated with success. Equally important is the feedback process, which allows institutions to understand how interventions, from mentoring to wellbeing check-ins or pathway adjustments, influence subsequent engagement and performance."
            },
            {
                type: "paragraph",
                text: "The StudyStash pilot study with the University of Birmingham has shown that such systems can enhance cognitive load management, student engagement and retention, especially when insights are paired with opportunities for human-led intervention (StudyStash, 2025)."
            },
            {
                type: "heading",
                level: 2,
                text: "Spotting early welfare signals"
            },
            {
                type: "paragraph",
                text: "Retention challenges are rarely academic alone. Declining engagement often stems from wellbeing, financial or social factors. When learning analytics integrate academic performance with behavioural and welfare indicators, institutions can better detect the early signs of difficulty."
            },
            {
                type: "paragraph",
                text: "For instance, a sustained reduction in study sessions accompanied by longer completion times might suggest discouragement or overwhelm. A pattern of late submissions or repeated attempts could indicate stress or insufficient foundational understanding. Similarly, reduced participation in collaborative or peer-based modules may reflect isolation or disengagement from the academic community."
            },
            {
                type: "paragraph",
                text: "Interpreted responsibly, these signals allow universities to intervene early and appropriately, connecting students to academic or welfare support before disengagement becomes withdrawal."
            },
            {
                type: "heading",
                level: 2,
                text: "Ethics, privacy and trust"
            },
            {
                type: "paragraph",
                text: "Using learning data to support students demands rigorous ethical safeguards. Responsible practice begins with anonymisation and role-based access controls, ensuring that only authorised staff can view sensitive information. It also requires clear consent processes, so students understand how their data is used and when it might trigger an intervention."
            },
            {
                type: "paragraph",
                text: "Crucially, human oversight remains essential: algorithmic indicators should prompt discussion, not automatic remediation. Ongoing monitoring and fairness reviews, as recommended in recent educational-AI guidance such as EDUCAUSE's Working Group Paper, further strengthen transparency and accountability (EDUCAUSE, 2025). This ethical framework helps ensure that data-driven insights translate into trusted, human centred action."
            },
            {
                type: "heading",
                level: 2,
                text: "The future of retention"
            },
            {
                type: "paragraph",
                text: "The next phase of student success will depend on closing the feedback loop, not simply measuring continuation, but understanding why students stay, thrive, or leave. Adaptive and ethically grounded systems that integrate learning and welfare insight have the potential to transform how universities sustain student success."
            },
            {
                type: "paragraph",
                text: "With national continuation rates now below 90 per cent, the urgency is clear. Retention is no longer about keeping students enrolled at all costs; it is about enabling every learner to progress with confidence, support and purpose. Closing the teaching-learning loop is no longer just innovation – it is imperative."
            },
            {
                type: "sources",
                title: "References",
                items: [
                    {
                        id: "hepi-continuation-challenge",
                        title: "Solving the continuation challenge with engagement analytics",
                        authors: ["Higher Education Policy Institute"],
                        date: "2025, February 21",
                        url: "https://www.hepi.ac.uk/2025/02/21/solving-the-continuation-challenge-with-engagement-analytics/",
                        type: "article",
                        publication: "HEPI",
                        description: "Higher Education Policy Institute. (2025, February 21). Solving the continuation challenge with engagement analytics. HEPI. https://www.hepi.ac.uk/2025/02/21/solving-the-continuation-challenge-with-engagement-analytics/"
                    },
                    {
                        id: "complete-university-guide-rankings",
                        title: "University league tables and rankings",
                        authors: ["The Complete University Guide"],
                        date: "2026",
                        url: "https://www.thecompleteuniversityguide.co.uk/league-tables/rankings?tabletype=full-table",
                        type: "website",
                        publication: "The Complete University Guide",
                        description: "The Complete University Guide. (2026). University league tables and rankings. The Complete University Guide. https://www.thecompleteuniversityguide.co.uk/league-tables/rankings?tabletype=full-table"
                    },
                    {
                        id: "educause-ai-ethical-guidelines",
                        title: "AI ethical guidelines for higher education",
                        authors: ["EDUCAUSE"],
                        date: "2025, June",
                        url: "https://library.educause.edu/resources/2025/6/ai-ethical-guidelines",
                        type: "article",
                        publication: "EDUCAUSE",
                        description: "EDUCAUSE. (2025, June). AI ethical guidelines for higher education. EDUCAUSE. https://library.educause.edu/resources/2025/6/ai-ethical-guidelines"
                    },
                    {
                        id: "studystash-uob-pilot",
                        title: "StudyStash pilot study - University of Birmingham",
                        authors: ["StudyStash"],
                        date: "2025",
                        url: "https://drive.google.com/file/d/1wYYj2cNQXwrDKacbffYyXgx_5o7Eagr2/view",
                        type: "other",
                        publication: "StudyStash",
                        description: "StudyStash. (2025). StudyStash pilot study - University of Birmingham [Unpublished report]. StudyStash. https://drive.google.com/file/d/1wYYj2cNQXwrDKacbffYyXgx_5o7Eagr2/view"
                    }
                ]
            }
        ],
        seo: {
            title: "Closing the Teaching-Learning Loop in Learning Support Platforms | StudyStash",
            description: "How learning-intelligence platforms can convert study data into actionable insights, enabling timely intervention and tracking outcomes to improve student retention and welfare.",
            canonical: "https://studystash.com/about/blogs/closing-teaching-learning-loop"
        },
        relatedContent: ["eab-partnership-announcement", "university-of-birmingham"]
    },
    "audible-business-attraction-program": {
        id: "audible-business-attraction-program",
        type: "blog",
        category: "partnership",
        title: "StudyStash Joins Audible's Business Attraction Program",
        description: "StudyStash has been selected to join Audible's Business Attraction Program and will establish a new operational base in New Jersey to support US expansion.",
        slug: "audible-business-attraction-program",
        publishDate: "2026-03-23",
        readingTime: "4 min read",
        author: {
            name: "Joanna Williams",
            image: "/team/joanna_williams.jpeg"
        },
        hero: {
            image: "/audible-newark.jpg",
            imageAlt: "StudyStash joins Audible's Business Attraction Program"
        },
        contentBlocks: [
            {
                type: "heading",
                level: 1,
                text: "StudyStash Joins Audible's Business Attraction Program"
            },
            {
                type: "paragraph",
                text: "StudyStash has been selected to join Audible's Business Attraction Program, a competitive initiative supporting innovative, fast-growing companies relocating to or expanding within Newark, New Jersey."
            },
            {
                type: "paragraph",
                text: "As part of the program, StudyStash will be establishing a new operational base in New Jersey, marking a major step forward in our international growth and supporting our expansion across the US higher education market."
            },
            {
                type: "heading",
                level: 2,
                text: "About Audible and its Business Attraction Program"
            },
            {
                type: "paragraph",
                text: "Audible is a leading producer and provider of premium audio storytelling and a subsidiary of Amazon, headquartered in Newark, New Jersey. Since relocating to Newark in 2007, Audible has played a significant role in driving local economic growth, generating billions in economic activity and championing an inclusive culture within tech."
            },
            {
                type: "paragraph",
                text: "Launched in 2023, Audible's Business Attraction Program supports innovative companies looking to establish or grow their presence in Newark, New Jersey. The program provides founders with access to career-changing resources, including financial support for office space or relocation costs, executive mentorship, networking opportunities, and more to help remove barriers to scale and impact."
            },
            {
                type: "paragraph",
                text: "StudyStash is proud to be selected as part of this game-changing initiative."
            },
            {
                type: "heading",
                level: 2,
                text: "What this means for StudyStash"
            },
            {
                type: "paragraph",
                text: "This strategic collaboration provides a strong foundation for expanding StudyStash's presence in the US, creating an environment for collaboration and growth alongside an innovative industry leader."
            },
            {
                type: "paragraph",
                text: "This marks a significant milestone for StudyStash as we enter an exciting new phase of international growth. The support from Audible will play a key role in helping us scale in the US, while continuing to deliver secure, AI-powered learning tools that support students and educators worldwide."
            }
        ],
        seo: {
            title: "StudyStash Joins Audible's Business Attraction Program | StudyStash",
            description: "StudyStash joins Audible's Business Attraction Program and establishes a new operational base in New Jersey to support growth across the US higher education market.",
            canonical: "https://studystash.com/about/blogs/audible-business-attraction-program"
        },
        relatedContent: ["eab-partnership-announcement", "closing-teaching-learning-loop", "innovators-uncensored"]
    }
};
